import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Instagram, CreditCard } from 'lucide-react';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();
  
  const currentYear = new Date().getFullYear();
  
  // Quick Links
  const quickLinks = [
    { name: t('navigation.home'), path: '/' },
    { name: t('navigation.services'), path: '/services' },
    { name: t('navigation.pricing'), path: '/pricing' },
    { name: t('navigation.about'), path: '/about' },
    { name: t('navigation.contact'), path: '/contact' },
    { name: t('navigation.faq'), path: '/faq' },
  ];
  
  // Legal Links
  const legalLinks = [
    { name: t('footer.links.sitemap'), path: '/sitemap' },
  ];
  
  // Social Media Links
  const socialLinks = [
    { icon: <Facebook size={20} />, url: 'https://www.facebook.com/profile.php?id=100083012568417', ariaLabel: 'Facebook' },
    { icon: <Twitter size={20} />, url: 'https://twitter.com', ariaLabel: 'Twitter' },
    { icon: <Linkedin size={20} />, url: 'https://www.linkedin.com/company/accounting-helpdesk/', ariaLabel: 'LinkedIn' },
    { icon: <Instagram size={20} />, url: 'https://www.instagram.com/accountinghelpdesk_/profilecard/?igsh=MzlmbG90dXFpMndm', ariaLabel: 'Instagram' },
  ];
  
  return (
    <footer className="bg-gray-900 text-white pt-16 pb-8">
      <div className="container-custom">
        {/* Footer Top */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center mb-4">
            <img src="/logo.png" alt="logo" className="w-20 h-20" />
            </div>
            <p className="text-gray-400 mb-6">
              {t('footer.description')}
            </p>
            
            {/* Bouton Carte de Visite */}
            <div className="mb-6">
              <Link
                to={getLocalizedPath('/business-card')}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-accent text-white px-4 py-2 rounded-lg hover:from-primary-dark hover:to-accent-dark transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <CreditCard size={18} />
                <span className="font-medium">{t('footer.businessCard')}</span>
              </Link>
            </div>
            
            <div className="flex space-x-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.ariaLabel}
                  className="text-gray-400 hover:text-primary transition-colors duration-200"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>
          
          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.quickLinks')}</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={getLocalizedPath(link.path)}
                    className="text-gray-400 hover:text-primary transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('contact.title')}</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin size={20} className="text-primary flex-shrink-0 mt-1" />
                <span className="text-gray-400">
                  Luxembourg
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Mail size={20} className="text-primary flex-shrink-0" />
                <a 
                  href="mailto:ahdluxembourg@gmail.com" 
                  className="text-gray-400 hover:text-primary transition-colors duration-200"
                >
                  {t('contact.info.emailValue')}
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Phone size={20} className="text-primary flex-shrink-0" />
                <a 
                  href="tel:+352621350801" 
                  className="text-gray-400 hover:text-primary transition-colors duration-200"
                >
                  {t('contact.info.phoneValue')}
                </a>
              </li>
            </ul>
          </div>
          
          {/* Legal Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.legal')}</h3>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={getLocalizedPath(link.path)}
                    className="text-gray-400 hover:text-primary transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        {/* Footer Bottom */}
        <div className="pt-8 border-t border-gray-800 text-center text-gray-500">
          <p>
            {t('footer.copyright').replace('2025', currentYear.toString())}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;